/**
 * Formatting helpers (FE-03 FR-FE03-21…25). Every helper takes the UI language explicitly; dates
 * always use America/Costa_Rica; the currency symbol comes from the data, never the language.
 */

export type FormatLanguage = "es-CR" | "en";

export const CR_TIME_ZONE = "America/Costa_Rica";

export interface Money {
  amount_minor: number;
  currency: string;
}

const SYMBOLS: Record<string, string> = { CRC: "₡", USD: "US$" };
const NBSP = String.fromCharCode(0xa0);
const MINUS = "−";

/** `{12000000, CRC}` → "₡120 000" (es-CR) / "₡120,000" (en); `{6500, USD}` → "US$65,00" / "US$65.00". */
export function formatMoney(money: Money, lang: FormatLanguage): string {
  const { amount_minor, currency } = money;
  const abs = Math.abs(amount_minor);
  const whole = abs % 100 === 0;
  const digits = currency === "CRC" && whole ? 0 : 2;
  const number = new Intl.NumberFormat(lang, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: "always",
  }).format(abs / 100);
  const symbol = SYMBOLS[currency];
  const body = symbol ? `${symbol}${number}` : `${currency} ${number}`;
  return amount_minor < 0 ? `${MINUS}${body}` : body;
}

/** Whole major units, rounded: `{23762, USD}` → "US$238" (the "≈" equivalents of FE-13). */
export function formatMoneyWhole(money: Money, lang: FormatLanguage): string {
  const units = Math.round(Math.abs(money.amount_minor) / 100);
  const number = new Intl.NumberFormat(lang, {
    maximumFractionDigits: 0,
    useGrouping: "always",
  }).format(units);
  const symbol = SYMBOLS[money.currency];
  const body = symbol ? `${symbol}${number}` : `${money.currency} ${number}`;
  return money.amount_minor < 0 ? `${MINUS}${body}` : body;
}

export type DateStyle = "date" | "dateLong" | "monthYear" | "weekday";

const DATE_OPTIONS: Record<FormatLanguage, Record<DateStyle, Intl.DateTimeFormatOptions>> = {
  "es-CR": {
    date: { day: "2-digit", month: "2-digit", year: "numeric" },
    dateLong: { day: "numeric", month: "long", year: "numeric" },
    monthYear: { month: "long", year: "numeric" },
    weekday: { weekday: "long", day: "numeric", month: "long" },
  },
  en: {
    date: { month: "short", day: "numeric", year: "numeric" },
    dateLong: { month: "long", day: "numeric", year: "numeric" },
    monthYear: { month: "long", year: "numeric" },
    weekday: { weekday: "long", month: "long", day: "numeric" },
  },
};

const PLAIN_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parse an API value: a plain `YYYY-MM-DD` (no zone shift) or an ISO timestamp. */
function toDate(value: string | Date): { date: Date; plain: boolean } {
  if (value instanceof Date) return { date: value, plain: false };
  const m = PLAIN_DATE.exec(value);
  if (m) return { date: new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, 12)), plain: true };
  return { date: new Date(value), plain: false };
}

/** Plain dates are shown as-is; timestamps in Costa Rica time (FE-03 FR-FE03-23). */
export function formatDate(
  value: string | Date,
  lang: FormatLanguage,
  style: DateStyle = "date",
): string {
  const { date, plain } = toDate(value);
  return new Intl.DateTimeFormat(lang, {
    ...DATE_OPTIONS[lang][style],
    timeZone: plain ? "UTC" : CR_TIME_ZONE,
  }).format(date);
}

/** "2:30 p. m." / "2:30 PM", in Costa Rica time. */
export function formatTime(value: string | Date, lang: FormatLanguage): string {
  return new Intl.DateTimeFormat(lang, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: CR_TIME_ZONE,
  }).format(toDate(value).date);
}

/** 24-hour time in Costa Rica time ("14:30"), for sentences where "p. m." would read badly (FE-15). */
export function formatTime24(value: string | Date, lang: FormatLanguage): string {
  return new Intl.DateTimeFormat(lang, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: CR_TIME_ZONE,
  }).format(toDate(value).date);
}

/** A number with a fixed range of decimals ("99,9" es-CR / "99.9" en). */
export function formatDecimal(
  value: number,
  lang: FormatLanguage,
  digits: { min?: number; max: number },
): string {
  return new Intl.NumberFormat(lang, {
    minimumFractionDigits: digits.min ?? 0,
    maximumFractionDigits: digits.max,
  }).format(value);
}

/** Date + time in Costa Rica time ("25/10/2026 10:30 p. m."). */
export function formatDateTime(value: string | Date, lang: FormatLanguage): string {
  return `${formatDate(value, lang)} ${formatTime(value, lang)}`;
}

const RELATIVE_STEPS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

/** "hace 2 horas" / "2 hours ago"; `now` defaults to the device clock. */
export function formatRelative(
  value: string | Date,
  lang: FormatLanguage,
  now: Date = new Date(),
): string {
  const seconds = Math.round((toDate(value).date.getTime() - now.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
  for (const [unit, size] of RELATIVE_STEPS) {
    if (Math.abs(seconds) >= size || unit === "second") {
      return rtf.format(Math.round(seconds / size), unit);
    }
  }
  return rtf.format(0, "second");
}

export function formatNumber(value: number, lang: FormatLanguage): string {
  return new Intl.NumberFormat(lang).format(value);
}

/** `0.8` → "80 %" (es-CR) / "80%" (en). */
export function formatPercent(ratio: number, lang: FormatLanguage): string {
  // ICU prints "80%" for es-CR; the Spanish style guide wants a (no-break) space.
  const number = new Intl.NumberFormat(lang, { maximumFractionDigits: 1 }).format(ratio * 100);
  return lang === "en" ? `${number}%` : `${number}${NBSP}%`;
}

/** Metres → "350 m" / "1,2 km" (the "a … / … away" wording lives in the catalogs). */
export function formatDistance(metres: number, lang: FormatLanguage): string {
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${new Intl.NumberFormat(lang, { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(metres / 1000)} km`;
}

/** CR numbers are stored as 8 digits and shown as 8888-8888 in both languages (FR-FE03-25). */
export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("506") ? digits.slice(3) : digits;
  if (national.length !== 8) return value;
  return `${national.slice(0, 4)}-${national.slice(4)}`;
}

/** `tel:` link for a CR number (`tel:+50688880000`), or null when it is not one. */
export function phoneHref(value: string): string | null {
  const digits = value.replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("506") ? digits.slice(3) : digits;
  return national.length === 8 ? `tel:+506${national}` : null;
}

/** Today's calendar date in Costa Rica (`YYYY-MM-DD`), whatever the device time zone. */
export function todayInCostaRica(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: CR_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** Whole days from `today` to `date` (both `YYYY-MM-DD`); negative when past. */
export function daysBetween(today: string, date: string): number {
  const ms = Date.parse(`${date}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/**
 * A calendar date relative to `today` (FE-03 FR-FE03-23): "hoy", "mañana", "ayer", "en 3 días" /
 * "today", "tomorrow", "in 3 days". `today` is the business date (`me.today` when the API gives it).
 */
export function formatRelativeDay(date: string, today: string, lang: FormatLanguage): string {
  return new Intl.RelativeTimeFormat(lang, { numeric: "auto" }).format(
    daysBetween(today, date),
    "day",
  );
}
